import { bopApi, cryptoInfo } from '@/lib/api/client';

const COIN_FEES_PATH = 'v1/fees';
const EXCLUSIONS_PATH = 'v1/fees/exclusions';
const TRADING_PATH = 'v1/fees/trading';

// Canonical home is utils/validation.ts (the exact-length rationale lives there).
// Temporary re-export: fee-exclusion-dialog.tsx still imports from here — remove once dialogs migrate.
export { ACCOUNT_HOLDER_ID_LENGTH } from '@/utils/validation';

/** Negative allowed, max 4 decimal places. */
export const TRADING_FEE_PATTERN = /^-?\d*(\.\d{0,4})?$/;

export const FEE_TYPES = ['deposit', 'withdrawal'] as const;
export type FeeType = (typeof FEE_TYPES)[number];

export const COIN_FEE_CATEGORIES = ['fiat', 'crypto'] as const;
export type CoinFeeCategory = (typeof COIN_FEE_CATEGORIES)[number];

/** One row of GET {CRYPTO_INFO_BASE_URL}/v1/fees. Fees are raw numbers — format in cells. */
export interface CoinFee {
  /** lower-case code, e.g. "btc", "eur" */
  coin: string;
  type: CoinFeeCategory;
  /** fee in NATIVE coin units */
  depositFee: number;
  withdrawalFee: number;
  /** same fee expressed in EUR */
  depositFeeEur: number;
  withdrawalFeeEur: number;
}

/**
 * KEEP, flagged for security review: the legacy Nuxt route called this
 * with NO authorization header — it is public read-only fee data, fetched here
 * directly from the browser via the unauthenticated crypto-info client.
 */
export const getCoinFees = () => cryptoInfo.get(COIN_FEES_PATH).json<CoinFee[]>();

/** One fetch, split client-side into the Fiat/Crypto tabs. */
export const filterCoinFeesByCategory = (fees: CoinFee[], category: CoinFeeCategory): CoinFee[] => fees.filter((fee) => fee.type === category);

export interface UpdateCoinFeeRequest {
  /** UPPER-CASE coin code, e.g. "BTC" */
  symbol: string;
  /** audit justification */
  comment: string;
  depositFeeEur?: number;
  withdrawalFeeEur?: number;
}

export interface UpdateCoinFeeParams {
  coin: string;
  feeType: FeeType;
  /** validated text-field value */
  amount: string;
  comment: string;
}

/**
 * Symbol is the UPPER-CASED coin; EXACTLY ONE of the two fee fields is
 * sent (the UI edits one fee type at a time), Number()-coerced — legacy sent the
 * raw string and the lambda parseFloat'ed it.
 */
export const buildCoinFeeUpdateRequest = ({ coin, feeType, amount, comment }: UpdateCoinFeeParams): UpdateCoinFeeRequest => ({
  symbol: coin.toUpperCase(),
  comment,
  ...(feeType === 'deposit' ? { depositFeeEur: Number(amount) } : { withdrawalFeeEur: Number(amount) }),
});

/** /v1/fees 400s with this code when both fee fields are falsy — a lone €0 fee cannot be set today. */
export const MISSING_FEES_ERROR_CODE = 'MISSING_FEES';

/**
 * The lambda swallows internal exceptions and still returns 200 {} —
 * never treat the status alone as proof the update landed; refetch and verify.
 */
export const updateCoinFee = (body: UpdateCoinFeeRequest) => bopApi.post(COIN_FEES_PATH, { json: body }).text();

/**
 * Field asymmetry: the LIST returns `pid`, but add/delete SEND
 * `accountHolderId` (same value).
 */
export interface FeeExclusion {
  /** account holder id (UUID) */
  pid: string;
  /** display name entered when the exclusion was added */
  name: string;
}

interface GetFeeExclusionsResponse {
  exclusions?: FeeExclusion[];
}

/** The Nuxt layer unwrapped `.exclusions || []` — do the same. */
export const unwrapFeeExclusions = (response: GetFeeExclusionsResponse): FeeExclusion[] => response.exclusions ?? [];

export const getFeeExclusions = () => bopApi.get(EXCLUSIONS_PATH).json<GetFeeExclusionsResponse>();

export interface AddFeeExclusionRequest {
  /** the PID */
  accountHolderId: string;
  name: string;
  comment: string;
}

/** Swallow caveat applies: 200 {} even after internal failures — refetch to verify. */
export const addFeeExclusion = (body: AddFeeExclusionRequest) => bopApi.post(EXCLUSIONS_PATH, { json: body }).text();

export interface DeleteFeeExclusionRequest {
  accountHolderId: string;
  comment: string;
}

/**
 * Chosen fix — deliberate feature addition: the legacy UI never wired
 * this endpoint (disabled trash, no Nuxt route) but it exists and works upstream.
 * The swallow caveat applies here too.
 */
export const deleteFeeExclusion = (body: DeleteFeeExclusionRequest) => bopApi.delete(EXCLUSIONS_PATH, { json: body }).text();

export interface CustomTradingFee {
  accountHolderId: string;
  /**
   * KEEP tolerant parsing: percent values stored verbatim as
   * written — records created via this page arrive as STRINGS ("0.05"), other
   * writers may have stored numbers. Render as-is; String()-normalise into
   * forms; Number()-coerce before arithmetic.
   */
  makerFeeSpot: string | number;
  takerFeeSpot: string | number;
  makerFeeFutures: string | number;
  takerFeeFutures: string | number;
  /** 'BOP' when set through this page */
  source?: string;
}

interface GetCustomTradingFeesResponse {
  data?: CustomTradingFee[];
}

/** The Nuxt layer unwrapped `data ?? []` — do the same. */
export const unwrapCustomTradingFees = (response: GetCustomTradingFeesResponse): CustomTradingFee[] => response.data ?? [];

export const getCustomTradingFees = () => bopApi.get(TRADING_PATH).json<GetCustomTradingFeesResponse>();

export interface CustomTradingFeeValues {
  makerFeeSpot: string;
  takerFeeSpot: string;
  makerFeeFutures: string;
  takerFeeFutures: string;
}

export interface SetCustomTradingFeeRequest extends CustomTradingFeeValues {
  comment: string;
}

/**
 * Chosen fix: fees MUST go over the wire as STRINGS — the
 * lambda rejects any individually falsy fee with MISSING_FEES, so numeric 0
 * fails but "0" passes. Legacy Nuxt did `.toString()` on each value.
 */
export const buildSetCustomTradingFeeRequest = (values: CustomTradingFeeValues, comment: string): SetCustomTradingFeeRequest => ({
  comment,
  makerFeeSpot: String(values.makerFeeSpot),
  takerFeeSpot: String(values.takerFeeSpot),
  makerFeeFutures: String(values.makerFeeFutures),
  takerFeeFutures: String(values.takerFeeFutures),
});

/** Body→path move: legacy sent accountHolderId in the Nuxt body; the direct upsert keys on the PATH. */
export const setCustomTradingFees = (accountHolderId: string, body: SetCustomTradingFeeRequest) => bopApi.post(`${TRADING_PATH}/${accountHolderId}`, { json: body }).text();

/** A DELETE with a JSON body — the lambda requires the comment there. */
export const deleteCustomTradingFees = (accountHolderId: string, comment: string) => bopApi.delete(`${TRADING_PATH}/${accountHolderId}`, { json: { comment } }).text();

/**
 * Strips only an ALL-zero fractional part ("5.000" → "5")
 * and leaves mixed fractions untouched ("5.100" stays "5.100"). Deliberately
 * NOT utils/format's truncateZeros, which trims trailing zeros ("5.100" → "5.1").
 */
export const stripAllZeroFraction = (value: number | string): string => {
  const text = value.toString();
  if (!text.includes('.')) return text;
  const [integer = text, fraction = ''] = text.split('.');
  return /^0+$/.test(fraction) ? integer : text;
};
